import { type Actor, type ItemOf } from '@shared/board'
import { clsx } from 'clsx'
import {
  ChevronRight,
  MessagesSquare,
  Pencil,
  Reply,
  Send,
  SquarePlus,
  TextQuote,
} from 'lucide-react'
import { type ReactNode, useState, useSyncExternalStore } from 'react'
import { useI18n } from '@/i18n/i18n'
import { Avatar } from '@/components/Avatar'
import { labelOf } from '@/session/describe'
import { clearQuoteHighlight, highlightQuote } from '@/session/focus'
import { HandToAiMenu } from '@/session/HandToAiMenu'
import { NoteText } from '@/session/SayText'
import { useSessionTitle } from '@/session/session-title'
import { DRAG_HANDLE, NO_DRAG } from '../classes'
import { useBoardCtx } from '../context'
import { useBoardDerived } from '../derived'
import { MessageComposer } from '../MessageComposer'
import { Author } from './Author'

const actionClass = clsx(
  NO_DRAG,
  'grid size-7 place-items-center rounded-md text-ink-2 hover:bg-surface-2 hover:text-ink',
)

function Action({
  label,
  onClick,
  children,
}: {
  label: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className={actionClass}
    >
      {children}
    </button>
  )
}

/** "«quoted words»" or "About Card A, Card B": what the message points at. */
function AnchorLine({ item }: { item: ItemOf<'message'> }) {
  const { client, actions } = useBoardCtx()
  const { t } = useI18n()
  const sessionTitle = useSessionTitle()
  const snap = useSyncExternalStore(client.subscribe, client.getSnapshot)
  const anchor = item.data.anchor
  if (!anchor) return null
  const live = anchor.items.filter((id) => snap.state.items.has(id))
  const names = live.map(
    (id) => labelOf(snap.state, client.doc, id, sessionTitle) || t('anchor.untitled'),
  )
  const quote = anchor.quote
  const target = anchor.items[0]
  return (
    <button
      type="button"
      disabled={!live.length}
      onClick={(e) => {
        e.stopPropagation()
        actions.current?.showItems(live)
        if (quote && target)
          setTimeout(() => {
            highlightQuote(target, quote)
          }, 450)
      }}
      onMouseEnter={() => {
        if (quote && target) highlightQuote(target, quote, null)
      }}
      onMouseLeave={clearQuoteHighlight}
      title={live.length ? t('anchor.show') : t('anchor.gone')}
      className={clsx(
        NO_DRAG,
        'mb-1 flex max-w-full items-start gap-1 rounded-sm text-left text-[12px] leading-snug',
        live.length ? 'text-ink-2 hover:text-ink' : 'text-muted line-through',
      )}
    >
      <TextQuote size={12} className="mt-[3px] flex-none opacity-70" />
      <span className="min-w-0 truncate">
        {quote
          ? `«${quote.replace(/\s+/g, ' ')}»`
          : live.length
            ? t('anchor.about', { names: names.join('、') })
            : t('anchor.gone')}
      </span>
    </button>
  )
}

/** Under the first message of a conversation: how many replies, and fold/unfold (for this viewer). */
function ThreadFooter({ item }: { item: ItemOf<'message'> }) {
  const { actions } = useBoardCtx()
  const { t } = useI18n()
  const { threads, rootOf, folded } = useBoardDerived()
  const thread = rootOf.get(item.id) === item.id ? threads.get(item.id) : undefined
  if (!thread?.replies.length) return null
  const isFolded = folded.has(item.id)
  const who: Actor[] = []
  for (const r of thread.replies) {
    const a = r.data.author
    if (!who.some((w) => w.kind === a.kind && w.id === a.id)) who.push(a)
  }
  return (
    <button
      type="button"
      aria-expanded={!isFolded}
      onClick={(e) => {
        e.stopPropagation()
        actions.current?.toggleThread(item.id)
      }}
      className={clsx(
        NO_DRAG,
        'mt-1.5 -mb-0.5 flex items-center gap-1.5 rounded-md py-0.5 pr-1.5 text-[12px] font-medium text-ink-2 hover:text-ink',
      )}
    >
      <ChevronRight size={13} className={clsx('transition-transform', !isFolded && 'rotate-90')} />
      <span className="flex -space-x-1">
        {who.slice(0, 4).map((a) => (
          <Avatar key={`${a.kind}:${a.id}`} actor={a} size={14} className="ring-1 ring-surface" />
        ))}
      </span>
      {isFolded
        ? t('thread.replies', { n: thread.replies.length })
        : t('thread.fold', { n: thread.replies.length })}
    </button>
  )
}

/** The words as markdown, with board pointers (links, or an embed written by mistake) as chips. */
function MessageText({ text }: { text: string }) {
  return (
    <div className="text-[13.5px] leading-relaxed text-ink">
      <NoteText text={text} chipClass={NO_DRAG} />
    </div>
  )
}

/**
 * Something said at a moment in time. A person's message starts out not handed
 * to any AI session: it shows a small marker, can be handed over, and until
 * then its author can still rewrite it (the board enforces this). Once handed
 * over it has been said: it can move, never change. It may point at things (its anchor) and start
 * or continue a thread. Its actions sit inside its own box, so they never
 * cover the messages around it.
 */
export function MessageView({ item, selected }: { item: ItemOf<'message'>; selected: boolean }) {
  const { relativeTime, t } = useI18n()
  const { sessions, actions, client, me } = useBoardCtx()
  const snap = useSyncExternalStore(sessions.subscribe, sessions.getSnapshot)
  const author = item.data.author
  const fromAi = author.kind === 'session'
  const handled = snap.fired[item.id] ?? []
  const unfired = !fromAi && handled.length === 0
  const sessionId = item.data.sessionId ?? handled.at(-1) ?? null
  const showInChat = () => {
    if (sessionId) sessions.reveal(sessionId, item.id, true)
  }
  const mine = author.kind === me.kind && author.id === me.id
  const editable = mine && unfired && !client.readOnly
  const [editing, setEditing] = useState(false)
  if (editing && editable)
    return (
      <MessageComposer
        width={item.w}
        initial={item.data.text}
        placeholder={t('composer.placeholder')}
        onCancel={() => {
          setEditing(false)
        }}
        onSubmit={(text, handToAi) => {
          setEditing(false)
          if (text !== item.data.text)
            client.apply([{ type: 'item.update', id: item.id, patch: { data: { text } } }])
          if (handToAi) sessions.fire(null, [item.id])
        }}
      />
    )
  return (
    <div
      data-message-id={item.id}
      className={clsx(
        'moot-node group/msg ' +
          DRAG_HANDLE +
          ' relative cursor-grab rounded-[4px_14px_14px_14px] border px-3 py-2 shadow-sm active:cursor-grabbing',
        fromAi ? 'border-line bg-surface' : 'border-(--note-line) bg-(--note)',
        selected && 'ring-2 ring-(--focus) ring-offset-1 ring-offset-canvas',
      )}
      style={{ width: item.w }}
    >
      <div className="mb-0.5 flex items-center justify-between gap-2">
        {sessionId ? (
          <button
            type="button"
            title={t('message.inChat')}
            onClick={(e) => {
              e.stopPropagation()
              showInChat()
            }}
            className={clsx(NO_DRAG, 'min-w-0 rounded-sm hover:underline')}
          >
            <Author actor={author} className="min-w-0" />
          </button>
        ) : (
          <Author actor={author} className="min-w-0" />
        )}
        {/* Gives way to the actions while they are up. */}
        <span className="flex flex-none items-center gap-1.5 transition-opacity group-focus-within/msg:opacity-0 group-hover/msg:opacity-0">
          {handled.length > 0 && (
            <span className="flex -space-x-1" title={t('hand.handledBy')}>
              {handled.map((sid) => (
                <Avatar
                  key={sid}
                  actor={{ kind: 'session', id: sid }}
                  size={14}
                  className="ring-1 ring-(--note)"
                />
              ))}
            </span>
          )}
          {unfired && (
            <span
              className="rounded-full border border-dashed border-muted/60 px-1.5 text-[10.5px] leading-4 text-muted"
              title={t('hand.unfiredHint')}
            >
              {t('hand.unfired')}
            </span>
          )}
          <time
            className="text-[11px] text-muted"
            dateTime={new Date(item.createdAt).toISOString()}
          >
            {relativeTime(item.createdAt)}
          </time>
        </span>
      </div>
      <AnchorLine item={item} />
      <div
        onDoubleClick={(e) => {
          if (!editable) return
          e.stopPropagation()
          setEditing(true)
        }}
      >
        <MessageText text={item.data.text} />
      </div>
      <ThreadFooter item={item} />

      {/* Over its own header while hovered or focused: covers nothing outside this message. */}
      <div
        role="toolbar"
        aria-label={t('message.actions')}
        className={clsx(
          'absolute top-1 right-1 flex items-center gap-0.5 rounded-lg border border-line p-0.5 opacity-0 shadow-sm transition-opacity group-hover/msg:opacity-100 focus-within:opacity-100',
          fromAi ? 'bg-surface' : 'bg-(--note)',
        )}
      >
        {!client.readOnly && (
          <>
            <Action
              label={t('message.reply')}
              onClick={() => {
                actions.current?.reply(item.id)
              }}
            >
              <Reply size={14} />
            </Action>
            {editable && (
              <Action
                label={t('message.edit')}
                onClick={() => {
                  setEditing(true)
                }}
              >
                <Pencil size={14} />
              </Action>
            )}
            <Action
              label={t('message.toCard')}
              onClick={() => {
                actions.current?.cardFromMessage(item.id)
              }}
            >
              <SquarePlus size={14} />
            </Action>
          </>
        )}
        {sessionId && (
          <Action label={t('message.inChat')} onClick={showInChat}>
            <MessagesSquare size={14} />
          </Action>
        )}
        {unfired && !client.readOnly && (
          <HandToAiMenu messageIds={[item.id]}>
            <button
              type="button"
              aria-label={t('hand.action')}
              title={t('hand.action')}
              className={actionClass}
            >
              <Send size={14} />
            </button>
          </HandToAiMenu>
        )}
      </div>
    </div>
  )
}
